import React, { useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
} from "@mui/material";

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    setMessage("");

    if (!token) {
      setStatus("error");
      setMessage(t("resetPassword.missingToken"));
      return;
    }
    if (password !== confirmPassword) {
      setStatus("error");
      setMessage(t("resetPassword.passwordMismatch"));
      return;
    }
    if (password.length < 8) {
      setStatus("error");
      setMessage(t("resetPassword.passwordLength"));
      return;
    }

    setStatus("loading");

    fetch(`${process.env.REACT_APP_BASE_URL}/auth/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, password }),
    })
      .then(async (response) => {
        const json = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(json?.message || t("resetPassword.error"));
        }
        setStatus("success");
      })
      .catch((err) => {
        setStatus("error");
        setMessage(err?.message || t("resetPassword.error"));
      });
  };

  return (
    <Container maxWidth="sm" sx={{ pt: 14, pb: 8 }}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 700 }}>
            {t("resetPassword.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t("resetPassword.subtitle")}
          </Typography>

          {status === "success" ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t("resetPassword.success")}
            </Alert>
          ) : null}

          {status === "error" ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {message || t("resetPassword.error")}
            </Alert>
          ) : null}

          {status !== "success" ? (
            <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label={t("resetPassword.newPassword")}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
                disabled={!token || status === "loading"}
              />
              <TextField
                label={t("resetPassword.confirmPassword")}
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                fullWidth
                disabled={!token || status === "loading"}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={!token || status === "loading"}
              >
                {status === "loading" ? t("resetPassword.submitting") : t("resetPassword.submit")}
              </Button>
            </Box>
          ) : null}

          <Button component={RouterLink} to="/SignInPage" variant="outlined" sx={{ mt: 2 }}>
            {t("resetPassword.goLogin")}
          </Button>
        </CardContent>
      </Card>
    </Container>
  );
}
